import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { publicAPI } from '../services/api';
import { useIntersect } from '../hooks/useIntersect';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';

function PageHero({ title, sub, label }) {
  return (
    <section style={{
      paddingTop: 'calc(var(--nav-h) + clamp(60px,8vw,100px))',
      paddingBottom: 'clamp(60px,8vw,100px)',
      background: 'linear-gradient(180deg, var(--bg-1) 0%, var(--bg-0) 100%)',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', bottom: -80, right: -80, width: 400, height: 400, background: 'radial-gradient(circle, rgba(232,164,58,0.05) 0%, transparent 60%)', pointerEvents: 'none' }}/>
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div className="section-label label" style={{ marginBottom: 20 }}>{label}</div>
        <h1 className="display-lg" style={{ marginBottom: 16 }}>{title}</h1>
        <p className="body-lg" style={{ color: 'var(--t-secondary)', maxWidth: 540 }}>{sub}</p>
      </div>
    </section>
  );
}

function ServiceCard({ service, idx }) {
  const [ref, visible] = useIntersect();
  const [hover, setHover] = useState(false);
  const hasCover = !!(service.media_url || service.thumbnail_url);

  return (
    <Link ref={ref} to={`/services/${service.slug}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="card"
      style={{
        padding: hasCover ? '0' : '28px', cursor: 'pointer',
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(24px)',
        transition: `opacity 0.5s ${idx * 0.05}s, transform 0.5s ${idx * 0.05}s, border-color 0.2s`,
        display: 'flex', flexDirection: 'column',
        position: 'relative', overflow: 'hidden',
        textDecoration: 'none',
      }}>

      {hasCover && (
        <div style={{ height: 160, overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
          <img
            src={(service.media_url || service.thumbnail_url)?.startsWith('http')
              ? (service.media_url || service.thumbnail_url)
              : `${MEDIA_URL}${service.media_url || service.thumbnail_url}`}
            alt={service.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: hover ? 'scale(1.04)' : 'scale(1)', transition: 'transform 0.4s' }}
            onError={e => { e.target.parentNode.style.display = 'none'; }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(13,15,18,0.7) 0%, transparent 60%)' }}/>
          <code style={{ position: 'absolute', top: 12, left: 12, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--gold)', background: 'rgba(13,15,18,0.75)', backdropFilter: 'blur(4px)', padding: '3px 8px', borderRadius: 4 }}>{service.sku}</code>
          {service.status === 'active'
            ? <span className="badge badge-teal" style={{ position: 'absolute', top: 12, right: 12, fontSize: '0.6rem' }}>Available</span>
            : <span className="badge badge-dim" style={{ position: 'absolute', top: 12, right: 12 }}>Coming Soon</span>
          }
        </div>
      )}

      <div style={{ padding: '20px 28px 28px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
        {!hasCover && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <code style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--gold)', background: 'rgba(232,164,58,0.1)', padding: '3px 8px', borderRadius: 4 }}>{service.sku}</code>
            {service.status === 'active'
              ? <span className="badge badge-teal" style={{ fontSize: '0.6rem' }}>Available</span>
              : <span className="badge badge-dim">Coming Soon</span>
            }
          </div>
        )}

        <div>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', fontWeight: 600, color: hover ? 'var(--gold)' : 'var(--t-primary)', transition: 'color 0.15s', marginBottom: 8, lineHeight: 1.25 }}>{service.name}</h3>
          <p className="body-sm" style={{ color: 'var(--t-secondary)', lineHeight: 1.65 }}>
            {service.description?.slice(0, 120)}{service.description?.length > 120 ? '…' : ''}
          </p>
        </div>

        {service.problem_solved && (
          <div style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 6, borderLeft: '2px solid var(--gold)', fontSize: '0.8rem', color: 'var(--t-muted)', fontStyle: 'italic' }}>
            "{service.problem_solved}"
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--t-muted)' }}>{service.category_name}</span>
          <span style={{ color: 'var(--gold)', fontSize: '0.85rem', fontWeight: 600 }}>
            {hover ? 'View Details →' : '→'}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function ServicesPage() {
  const [searchParams] = useSearchParams();
  const [categories, setCats]     = useState([]);
  const [services, setServices]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeCategory, setActiveCat] = useState(searchParams.get('cat') || '');
  const [search, setSearch]       = useState('');

  useEffect(() => {
    publicAPI.categories().then(r => setCats(r.data?.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeCategory) params.category = activeCategory;
      const res = await publicAPI.services(params);
      setServices(res.data?.data || []);
    } catch { setServices([]); } finally { setLoading(false); }
  }, [activeCategory]);

  useEffect(() => { load(); }, [load]);

  const filtered = services.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.sku.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHero
        label="Services Catalog"
        title={<>Everything You Need<br /><span className="gold">to Go Digital</span></>}
        sub="From idea to deployment — web, design, media, ICT, and strategy services built for African businesses."
      />

      {/* Controls */}
      <div style={{ background: 'var(--bg-0)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 'var(--nav-h)', zIndex: 50 }}>
        <div className="container" style={{ padding: '16px clamp(20px,5vw,60px)' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input placeholder="Search services or SKU…" value={search} onChange={e => setSearch(e.target.value)}
              style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', color: 'var(--t-primary)', padding: '9px 16px', fontSize: '0.875rem', width: 220 }}/>
            <button onClick={() => setActiveCat('')}
              style={{ padding: '8px 14px', borderRadius: 20, fontSize: '0.8rem', cursor: 'pointer', fontWeight: !activeCategory ? 600 : 400, background: !activeCategory ? 'var(--gold)' : 'var(--bg-2)', color: !activeCategory ? '#080a0d' : 'var(--t-secondary)', border: `1px solid ${!activeCategory ? 'var(--gold)' : 'var(--border)'}`, transition: 'all 0.15s' }}>All</button>
            {categories.map(c => (
              <button key={c.id} onClick={() => setActiveCat(activeCategory === c.slug ? '' : c.slug)}
                style={{ padding: '8px 14px', borderRadius: 20, fontSize: '0.8rem', cursor: 'pointer', fontWeight: activeCategory === c.slug ? 600 : 400, background: activeCategory === c.slug ? 'rgba(232,164,58,0.12)' : 'var(--bg-2)', color: activeCategory === c.slug ? 'var(--gold)' : 'var(--t-secondary)', border: `1px solid ${activeCategory === c.slug ? 'rgba(232,164,58,0.4)' : 'var(--border)'}`, transition: 'all 0.15s' }}>
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <section style={{ padding: 'clamp(40px,6vw,80px) 0', minHeight: '60vh' }}>
        <div className="container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 80, color: 'var(--t-muted)' }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 80 }}>
              <div style={{ fontSize: '2rem', marginBottom: 12, opacity: 0.3 }}>◈</div>
              <div style={{ color: 'var(--t-muted)' }}>No services found</div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 24, fontSize: '0.8rem', color: 'var(--t-muted)' }}>
                {filtered.length} service{filtered.length !== 1 ? 's' : ''}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
                {filtered.map((s, i) => <ServiceCard key={s.id} service={s} idx={i}/>)}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--bg-1)', borderTop: '1px solid var(--border)', padding: 'clamp(50px,6vw,80px) 0' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <h2 className="display-sm" style={{ marginBottom: 8 }}>Don't see what you need?</h2>
            <p style={{ color: 'var(--t-secondary)', fontSize: '0.95rem' }}>We build custom solutions. Tell us your challenge.</p>
          </div>
          <Link to="/contact?type=client" className="btn btn-primary">Start a Conversation →</Link>
        </div>
      </section>
    </div>
  );
}